import { useEffect, useRef } from "react";
import { Map, APIProvider, useMap } from "@vis.gl/react-google-maps";
import { useGeographicBoundaries } from "@/api/geographic-boundaries";
import type { GeographicBoundary } from "@/api.types.v2";

interface GeographicBoundariesMapProps {
  height?: string;
  selectedBoundaryId?: string;
  onBoundaryClick?: (boundary: GeographicBoundary) => void;
}

// Component to render polygons on the map
function BoundariesPolygons({
  boundaries,
  selectedBoundaryId,
  onBoundaryClick,
}: {
  boundaries: GeographicBoundary[];
  selectedBoundaryId?: string;
  onBoundaryClick?: (boundary: GeographicBoundary) => void;
}) {
  const map = useMap();
  const polygonsRef = useRef<google.maps.Polygon[]>([]);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const zoomListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  useEffect(() => {
    if (!map || !boundaries.length) return;

    // Clear existing polygons
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    polygonsRef.current = [];

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    // Close existing info window
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    // Create new polygons for each boundary
    const newPolygons: google.maps.Polygon[] = [];
    const markers: google.maps.Marker[] = [];
    const bounds = new google.maps.LatLngBounds();

    boundaries.forEach((boundary, index) => {
      if (!boundary.isActive) return; // Skip inactive boundaries

      if (boundary.boundary?.coordinates) {
        // Generate a color for each boundary
        const colors = [
          "#FF6B6B",
          "#4ECDC4",
          "#45B7D1",
          "#96CEB4",
          "#FFEAA7",
          "#DDA0DD",
          "#98D8C8",
          "#F7DC6F",
          "#BB8FCE",
          "#85C1E9",
        ];
        const color =
          boundary.id === selectedBoundaryId
            ? "#FF4444"
            : colors[index % colors.length];

        // Create info window content
        const createInfoContent = () => `
          <div style="padding: 8px; min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">${boundary.name}</h3>
            ${boundary.code ? `<p style="margin: 0 0 4px 0;"><strong>Code:</strong> ${boundary.code}</p>` : ""}
            <p style="margin: 0 0 4px 0;"><strong>Source:</strong> ${
              boundary.source === "osm" ? "OpenStreetMap" : boundary.source
            }</p>
            ${
              boundary.metadata?.population
                ? `<p style="margin: 0 0 4px 0;"><strong>Population:</strong> ${Number(boundary.metadata.population).toLocaleString()}</p>`
                : ""
            }
            ${(() => {
              const level = boundary.metadata?.adminLevel;
              if (level == null) return "";
              let levelStr: string;
              if (typeof level === "object") {
                levelStr = JSON.stringify(level);
              } else if (typeof level === "number") {
                levelStr = level.toString();
              } else if (typeof level === "string") {
                levelStr = level;
              } else {
                levelStr = JSON.stringify(level);
              }
              return `<p style="margin: 0 0 4px 0;"><strong>Admin Level:</strong> ${levelStr}</p>`;
            })()}
            <p style="margin: 0 0 4px 0;"><strong>Status:</strong> ${
              boundary.isActive
                ? '<span style="color: green;">Active</span>'
                : '<span style="color: gray;">Inactive</span>'
            }</p>
            <p style="margin: 0;"><strong>Type:</strong> ${boundary.boundary.type || "Polygon"}</p>
          </div>
        `;

        // Helper function to create a polygon with event listeners
        const createPolygon = (
          paths: google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][]
        ) => {
          const polygon = new google.maps.Polygon({
            paths,
            strokeColor: color,
            strokeOpacity: boundary.id === selectedBoundaryId ? 1 : 0.8,
            strokeWeight: boundary.id === selectedBoundaryId ? 3 : 2,
            fillColor: color,
            fillOpacity: boundary.id === selectedBoundaryId ? 0.4 : 0.2,
            map,
            clickable: true,
          });

          // Add click listener
          polygon.addListener("click", (event: google.maps.MapMouseEvent) => {
            // Close previous info window
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
            }

            // Create and open new info window
            const infoWindow = new google.maps.InfoWindow({
              content: createInfoContent(),
              position: event.latLng,
            });
            infoWindow.open(map);
            infoWindowRef.current = infoWindow;

            // Call the click handler if provided
            if (onBoundaryClick) {
              onBoundaryClick(boundary);
            }
          });

          return polygon;
        };

        // Handle both Polygon and MultiPolygon
        if (boundary.boundary.type === "Polygon") {
          // For Polygon: coordinates is an array of linear rings
          const paths = boundary.boundary.coordinates.map((ring: number[][]) =>
            ring.map((coord: number[]) => ({
              lat: coord[1], // GeoJSON uses [lng, lat], Google Maps uses {lat, lng}
              lng: coord[0],
            }))
          );

          const polygon = createPolygon(paths);
          newPolygons.push(polygon);

          // Extend bounds to include this boundary
          paths.forEach((ring) => {
            ring.forEach((point) => bounds.extend(point));
          });
        } else {
          // MultiPolygon: coordinates is an array of polygons
          boundary.boundary.coordinates.forEach(
            (polygonCoords: number[][][]) => {
              const paths = polygonCoords.map((ring: number[][]) =>
                ring.map((coord: number[]) => ({
                  lat: coord[1],
                  lng: coord[0],
                }))
              );

              const polygon = createPolygon(paths);
              newPolygons.push(polygon);

              // Extend bounds to include this polygon
              paths.forEach((ring) => {
                ring.forEach((point) => bounds.extend(point));
              });
            }
          );
        }
      }
    });

    polygonsRef.current = newPolygons;

    // Create markers for each boundary with custom labels
    boundaries.forEach((boundary) => {
      if (!boundary.isActive || !boundary.boundary?.coordinates) return;

      // Calculate the center of the boundary
      let centerLat = 0;
      let centerLng = 0;
      let pointCount = 0;

      if (boundary.boundary.type === "Polygon") {
        boundary.boundary.coordinates.forEach((ring: number[][]) => {
          ring.forEach((coord: number[]) => {
            centerLng += coord[0];
            centerLat += coord[1];
            pointCount++;
          });
        });
      } else {
        // MultiPolygon
        boundary.boundary.coordinates.forEach((polygon: number[][][]) => {
          polygon.forEach((ring: number[][]) => {
            ring.forEach((coord: number[]) => {
              centerLng += coord[0];
              centerLat += coord[1];
              pointCount++;
            });
          });
        });
      }

      if (pointCount > 0) {
        centerLat /= pointCount;
        centerLng /= pointCount;

        // Get current zoom level to set initial scale
        const currentZoom = map.getZoom() ?? 10;
        const getScaleForZoom = (zoom: number) => {
          // Scale markers based on zoom level
          if (zoom < 8) return 0.8;
          if (zoom < 10) return 1.0;
          if (zoom < 12) return 1.2;
          if (zoom < 14) return 1.5;
          return 1.8;
        };

        const getFontSizeForZoom = (zoom: number) => {
          // Scale font size based on zoom level
          if (zoom < 8) return "10px";
          if (zoom < 10) return "11px";
          if (zoom < 12) return "12px";
          if (zoom < 14) return "13px";
          return "14px";
        };

        // Create a custom marker with the boundary name
        const marker = new google.maps.Marker({
          position: { lat: centerLat, lng: centerLng },
          map: map,
          label: {
            text: boundary.name,
            color: "#FFFFFF",
            fontSize: getFontSizeForZoom(currentZoom),
            fontWeight: "bold",
            className: "boundary-label",
          },
          icon: {
            path: "M 0,0 C -2,-10 -10,-10 -10,0 -10,10 -2,10 0,0 Z",
            fillColor: "#333333",
            fillOpacity: 0.9,
            strokeColor: "#FFFFFF",
            strokeWeight: 1,
            scale: getScaleForZoom(currentZoom),
            labelOrigin: new google.maps.Point(0, 0),
          },
        });

        // Add click listener to show info window
        marker.addListener("click", () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }

          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">${boundary.name}</h3>
                ${boundary.code ? `<p style="margin: 0;"><strong>Code:</strong> ${boundary.code}</p>` : ""}
                <p style="margin: 0;"><strong>Source:</strong> ${
                  boundary.source === "osm" ? "OpenStreetMap" : boundary.source
                }</p>
              </div>
            `,
            position: { lat: centerLat, lng: centerLng },
          });
          infoWindow.open(map);
          infoWindowRef.current = infoWindow;

          if (onBoundaryClick) {
            onBoundaryClick(boundary);
          }
        });

        markers.push(marker);
      }
    });

    markersRef.current = markers;

    // Add zoom listener to update marker scales
    if (markers.length > 0) {
      // Remove existing listener if any
      if (zoomListenerRef.current) {
        google.maps.event.removeListener(zoomListenerRef.current);
      }

      // Add new zoom listener
      zoomListenerRef.current = map.addListener("zoom_changed", () => {
        const zoom = map.getZoom() ?? 10;
        const getScaleForZoom = (z: number) => {
          if (z < 8) return 0.8;
          if (z < 10) return 1.0;
          if (z < 12) return 1.2;
          if (z < 14) return 1.5;
          return 1.8;
        };

        const getFontSizeForZoom = (z: number) => {
          if (z < 8) return "10px";
          if (z < 10) return "11px";
          if (z < 12) return "12px";
          if (z < 14) return "13px";
          return "14px";
        };

        const scale = getScaleForZoom(zoom);
        const fontSize = getFontSizeForZoom(zoom);

        // Update all markers
        markersRef.current.forEach((marker) => {
          const currentIcon = marker.getIcon() as google.maps.Symbol;
          if (currentIcon) {
            marker.setIcon({
              ...currentIcon,
              scale: scale,
            });
          }
          const currentLabel = marker.getLabel() as google.maps.MarkerLabel;
          if (currentLabel) {
            marker.setLabel({
              ...currentLabel,
              fontSize: fontSize,
            });
          }
        });
      });
    }

    // Fit map to show all boundaries with padding
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }

    // Cleanup function
    return () => {
      polygonsRef.current.forEach((polygon) => polygon.setMap(null));
      polygonsRef.current = [];
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      if (zoomListenerRef.current) {
        google.maps.event.removeListener(zoomListenerRef.current);
        zoomListenerRef.current = null;
      }
    };
  }, [map, boundaries, selectedBoundaryId, onBoundaryClick]);

  return null;
}

export function GeographicBoundariesMap({
  height = "400px",
  selectedBoundaryId,
  onBoundaryClick,
}: GeographicBoundariesMapProps) {
  const { data: geographicBoundaries, isLoading } = useGeographicBoundaries();
  const defaultCenter = { lat: 43.6532, lng: -79.3832 }; // Toronto coordinates

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg"
        style={{ height }}
      >
        <div className="text-gray-500">Loading map...</div>
      </div>
    );
  }

  const activeBoundaries =
    geographicBoundaries?.filter((b) => b.isActive) ?? [];

  if (activeBoundaries.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 border rounded-lg"
        style={{ height }}
      >
        <div className="text-center text-gray-500">
          <p className="font-medium">
            No active geographic boundaries to display
          </p>
          <p className="text-sm mt-1">
            Fetch from OpenStreetMap or upload GeoJSON files to see boundaries
            on the map
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full border rounded-lg overflow-hidden shadow-xs"
      style={{ height }}
    >
      <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={defaultCenter}
          defaultZoom={10}
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: "100%", height: "100%" }}
          mapTypeControl={true}
          zoomControl={true}
          streetViewControl={false}
          fullscreenControl={true}
        >
          <BoundariesPolygons
            boundaries={activeBoundaries}
            selectedBoundaryId={selectedBoundaryId}
            onBoundaryClick={onBoundaryClick}
          />
        </Map>
      </APIProvider>
    </div>
  );
}
